import React, { useState, useEffect, useRef, Fragment } from "react";
import { useSearchParams } from "react-router-dom";
import { debounce } from "lodash";
import { useTranslation } from "react-i18next";
import { updateLanguageFromUrl } from "../../i18n/config";

import {
  Chart as ChartJS,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
  registerables,
  ChartData,
} from "chart.js";
import { Radar } from "react-chartjs-2";

import { CSSTransition, TransitionGroup } from "react-transition-group";

import botonImg from "../../assets/images/estilo-aprendizaje/boton.svg";

import {
  EstiloAprendizajeMainContainer,
  AffirmationContainer,
  RowContainer,
  ItemContainer,
  FirstItemContainer,
  ImageButton,
  ImageButtonContainer,
  ExplanationContainer,
  InstruccionesContainer,
  IntrospeccionLabel,
  IntrospeccionName,
  LogoContainer,
  EstiloAprendizajeHeader,
  TopHeader,
  FooterContainer,
  AllItemsContainer,
} from "./EstiloAprendizajeStyles";

import axios, { EnpointsUrlBase } from "../../helpers/axios";

import exampleImage from "../../assets/images/estilo-aprendizaje-ejemplo.png";

ChartJS.register(
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
  ...registerables
);

interface Result {
  idEncabezado: string;
  ca: number;
  ea: number;
  ec: number;
  or: number;
  estilo: string;
}

interface AnswerHeader {
  nombreUsuario: string;
  emailUsuario: string;
  empresa: string;
  programa: string;
  respuestas: Array<AnswerDetail>;
  intento: number;
}

interface AnswerDetail {
  AfirmacionId: string;
  Ec: number;
  Or: number;
  Ca: number;
  Ea: number;
}

interface AffirmationAnswer extends Affirmation {
  answerEc?: number;
  answerOr?: number;
  answerCa?: number;
  answerEa?: number;
}

interface Affirmation {
  id: string;
  order: number;
  textEc: string;
  textOr: string;
  textCa: string;
  textEa: string;
}

interface EstiloAprendizajeContainerProps {
  /** the affirmations that are gonna appear in the page */
  //affirmations: Array<Affirmation>;
}

enum EstiloAprendizajeTipos {
  EC = "ec",
  OR = "or",
  CA = "ca",
  EA = "ea",
}

const EstiloAprendizajeContainer: React.FC<
  EstiloAprendizajeContainerProps
> = () => {
  const { t } = useTranslation();
  const [answers, setAnswers] = useState<Array<AffirmationAnswer>>([]);
  const [result, setResult] = useState<Result>({
    idEncabezado: "00000000-0000-0000-0000-000000000000",
    ec: 0,
    ea: 0,
    or: 0,
    ca: 0,
    estilo: "",
  });
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [canCalculate, setCanCalculate] = useState<boolean>(false);
  const [calculating, setCalculating] = useState<boolean>(false);
  const [generating, setGenerating] = useState<boolean>(false);
  const [chartData, setChartData] = useState<any>({ datasets: [] });
  const [generateAuto, setGenerateAuto] = useState<boolean>(false);

  const modes: Array<string> = ["out-in", "in-out"];

  const nodeRef = React.useRef(null);
  const node1Ref = React.useRef(null);

  const [searchParams, setSearchParams] = useSearchParams();

  const nombreUsuario = searchParams.get("u") || "";
  const empresa = searchParams.get("e") || "";
  const email = searchParams.get("m") || "";
  const programa = searchParams.get("p") || "";
  const stopGeneratingAutomatically = (searchParams.get("hr") || "") === "1";
  const lang = searchParams.get("lang") || "es";

  let etapa = searchParams.get("ip") || "";
  const esPorEtapa =
    !isNaN(Number(etapa)) && Number(etapa) > 0 && Number(etapa) <= 3;

  //if (Number(etapa) > 0 && Number(etapa) <= 3 && !isNaN(Number(etapa))) {
  //cantidadPreguntas = (maxEtapas * Number(etapa));
  // console.log(cantidadPreguntas);
  //}

  // Actualizar idioma cuando cambie la URL
  useEffect(() => {
    updateLanguageFromUrl();
  }, [lang]);

  const title = t("header.introspection");
  const subtitle = (
    <div>
      {t("header.learningStyle")}{" "}
      <span>{t("header.learningStyleHighlight")} </span>
      {esPorEtapa ? t("header.stage", { stage: etapa }) : ""}
    </div>
  );

  const [affirmations, setAffirmations] = useState<Array<Affirmation>>([]);
  const [hasHeader, setHasHeader] = useState(true);

  const chartRef: any = useRef();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    //setGlobalTitle(title, subtitle);

    //http://localhost:3001/?u=Yeison&e=mancosoft&m=yeison.manco@mancosoft.com&p=programa1&ip=1

    const getPreguntas = () => {
      axios.get(
        `${EnpointsUrlBase}/EstiloAprendizaje/GetPreguntas?lang=${lang}`,
        (res: any, data: any) => {
          if (res === 200) {
            const affirmationsConverted = data.map((aff: any) => ({
              id: aff.id,
              textEc: aff.opcionEc,
              textOr: aff.opcionOr,
              textCa: aff.opcionCa,
              textEa: aff.opcionEa,
            }));
            setAffirmations(affirmationsConverted);
          }
          setLoading(false);
        }
      );
    };

    const getHeader = () => {
      axios.get(
        `${EnpointsUrlBase}/EstiloAprendizaje` + window.location.search,
        (res: any, data: any) => {
          if (res === 200) {
            if (
              data &&
              data.idEncabezadoRespuesta ===
                "00000000-0000-0000-0000-000000000000"
            )
              getPreguntas();
            else {
              setResult({
                idEncabezado: data.idEncabezadoRespuesta,
                ec: data.ec,
                ea: data.ea,
                or: data.or,
                ca: data.ca,
                estilo: data.estilo,
              });
              setCurrentStep(3);
              setLoading(false);
            }
          } else {
            console.log("Error calculating");
          }
        }
      );
    };

    getHeader();
  }, []);

  //la url tra ip cuando sea por etapa
  //http://localhost:3001/?u=Yeison&e=mancosoft&m=yeison.manco@mancosoft.com&p=programa1&ip=1

  //const data = {
  //    labels: ['CE', 'OR', 'CA', 'EA'],
  //datasets: [
  //    {
  //        label: '# of Votes',
  //        data: [2, 9, 10, 20],
  //        backgroundColor: 'rgba(255, 99, 132, 1)',
  //        borderColor: 'rgba(255, 99, 132, 1)',
  //        borderWidth: 1,
  //    },
  //],

  //};

  const paginate = (
    array: Array<any>,
    page_size: number,
    page_number: number
  ) => {
    return array.slice((page_number - 1) * page_size, page_number * page_size);
  };

  useEffect(() => {
    setChartData({
      labels: ["EC", "OR", "CA", "EA"],
      datasets: [
        {
          //label: "Student A",//divergente
          backgroundColor:
            result.estilo == "divergente"
              ? "rgba(221,172,82,0.4)"
              : "transparent",
          data: [result.ec, result.or, 0, 0], //[EC, OR, 0, 0], ,
          borderColor: "#ddac52",
          borderWidth: 2,
        },
        {
          borderColor: "#ddac52", //asimilador
          borderWidth: 2,
          backgroundColor:
            result.estilo == "asimilador"
              ? "rgba(221,172,82,0.4)"
              : "transparent",
          data: [0, result.or, result.ca, 0], //[0, OR, CA, 0]
        },
        {
          borderColor: "#ddac52", //convergente
          borderWidth: 2,
          backgroundColor:
            result.estilo == "convergente"
              ? "rgba(221,172,82,0.4)"
              : "transparent",
          data: [0, 0, result.ca, result.ea], //[0, 0, CA, EA]
        },
        {
          borderColor: "#ddac52", //acomodador
          borderWidth: 2,
          backgroundColor:
            result.estilo == "acomodador"
              ? "rgba(221,172,82,0.4)"
              : "transparent",
          data: [result.ec, 0, 0, result.ea], //[EC, 0, 0, EA]
        },
      ],
    });
  }, [result]);

  const options: any = {
    maintainAspectRatio: false,
    responsive: true,
    plugins: {
      tooltip: {
        enabled: false,
      },
      legend: {
        display: false,
      },
    },
    animation: {
      onComplete: () => {
        //console.log(chartRef?.current?.toBase64Image());

        //console.log(result.idEncabezado,"idEncabezado");
        if (
          result.idEncabezado != "" &&
          chartRef?.current?.toBase64Image() != null
        ) {
          axios.post(
            `${EnpointsUrlBase}/EstiloAprendizaje/GuardarGrafico`,
            {
              encabezadoId: result.idEncabezado,
              base64Imagen: chartRef?.current?.toBase64Image(),
            },
            (res: any, data: any) => {
              if (res === 200) {
                if (generateAuto && !generating && !calculating) {
                  debouncedHandleGeneratePdfButtonClick();
                }
                setGenerateAuto(false);
              } else {
                console.log("Error calculating");
              }
              setCalculating(false);
            }
          );
        }
      },
    },
    //scales: {
    //    y: {
    //        lineArc: true,
    //        ticks: {
    //            beginAtZero: true,
    //            min: 0,
    //            max: 120,
    //            stepSize: 20,
    //            //fontSize: 15,
    //            //fontStyle: "bold",
    //            //fontColor: "#263755",
    //            display: false,
    //            color: "#263755",
    //            font: {
    //                size: 15,
    //                weight: "bold"
    //            }
    //        },
    //        pointLabels: {
    //            fontSize: 18,
    //            fontColor: "#263755",
    //            fontStyle: "bold",
    //            font: {
    //                size: 18,
    //                weight: 'bold',
    //                color: '#263755'
    //            }
    //        },
    //        angleLines: {
    //            lineWidth: 2,
    //            color: "rgba(0,0,0,0.0)"
    //        },
    //        gridLines: {
    //            lineWidth: 2,
    //            color: "rgba(0,0,0,0.0)",
    //            circular: true
    //        }
    //    },
    //},
    scales: {
      r: {
        beginAtZero: true,
        min: 0,
        max: 120,
        display: true,
        pointLabels: {
          color: "#003a4b",
          font: {
            size: 18,
          },
        },
        angleLines: {
          display: true,
          lineWidth: 1,
          color: "black",
        },
        grid: {
          display: true,
          lineWidth: 1,
          color: "black",
          //circular: true,
        },
        ticks: {
          display: false,
          stepSize: 20,
        },
      },
    },
  };

  const [inAnimation, setInAnimation] = useState(true);

  useEffect(() => {
    if (affirmations.length > 0) {
      let affirmationsCopy = [...affirmations];
      if (esPorEtapa)
        affirmationsCopy = paginate(affirmationsCopy, 3, Number(etapa));
      setAnswers(
        affirmationsCopy.map((af) => ({
          ...af,
          answerEc: 0,
          answerOr: 0,
          answerCa: 0,
          answerEa: 0,
        }))
      );
    }
  }, [affirmations]);

  useEffect(() => {
    const canCalc = validateIfAllQuestionsWasAnswered(answers);
    setCanCalculate(canCalc);
  }, [answers]);

  useEffect(() => {
    if (calculating) {
      const respuestaToSend: AnswerHeader = {
        nombreUsuario: nombreUsuario,
        emailUsuario: email,
        empresa: empresa,
        programa: programa,
        intento: 1,
        respuestas: answers.map((answer: AffirmationAnswer) => ({
          AfirmacionId: answer.id,
          Ec: answer.answerEc || 0,
          Or: answer.answerOr || 0,
          Ca: answer.answerCa || 0,
          Ea: answer.answerEa || 0,
        })),
      };

      axios.post(
        `${EnpointsUrlBase}/EstiloAprendizaje` + window.location.search,
        respuestaToSend,
        (res: any, data: any) => {
          if (res === 200) {
            setResult({
              idEncabezado: data.idEncabezadoRespuesta,
              ec: data.ec,
              ea: data.ea,
              or: data.or,
              ca: data.ca,
              estilo: data.estilo,
            });
            setCurrentStep(3);

            setGenerateAuto(!stopGeneratingAutomatically);
          } else {
            console.log("Error calculating");
          }
          setCalculating(false);
        }
      );
    }
  }, [calculating]);

  const isNumeric = (e: React.KeyboardEvent<HTMLInputElement>) => {
    var result = false;
    try {
      var charCode = e.which ? e.which : e.keyCode;
      if (
        (charCode >= 49 && charCode <= 52) ||
        (charCode >= 97 && charCode <= 100) ||
        charCode == 46 ||
        charCode == 8 ||
        charCode == 9
      ) {
        result = true;
      }
    } catch (err) {
      //console.log(err);
    }
    return result;
  };

  const isNumber = (value?: number) =>
    typeof value === "number" &&
    isFinite(value) &&
    Number(value) >= 1 &&
    Number(value) <= 4;
  const validateIfAllQuestionsWasAnswered = (
    answersToCheck: Array<AffirmationAnswer>
  ) => {
    let toReturn: boolean = true;
    if (answersToCheck.length <= 0) return false;
    for (let i = 0; i < answersToCheck.length; i++) {
      if (
        !isNumber(answersToCheck[i].answerCa) ||
        !isNumber(answersToCheck[i].answerEa) ||
        !isNumber(answersToCheck[i].answerEc) ||
        !isNumber(answersToCheck[i].answerOr)
      ) {
        //console.log(answersToCheck);
        toReturn = false;
        break;
      }
    }
    return toReturn;
  };

  const isRepeatedNumber = (value: number, itemToFindIn: AffirmationAnswer) => {
    return (
      itemToFindIn.answerEc === value ||
      itemToFindIn.answerCa === value ||
      itemToFindIn.answerEa === value ||
      itemToFindIn.answerOr === value
    );
  };

  const handleChange = (
    affirmation: Affirmation,
    e: React.ChangeEvent<HTMLInputElement>,
    answerCategory: EstiloAprendizajeTipos
  ) => {
    const answer = answers.filter((k) => k.id == affirmation.id)[0];
    let valueAsNumber: any = Number(e.target.value);

    if (!isNumber(valueAsNumber) || isRepeatedNumber(valueAsNumber, answer)) {
      e.target.value = "";
      valueAsNumber = 0;
    }

    //if (e.target.value === "" || !isNumber(valueAsNumber) || isRepeatedNumber(valueAsNumber, answer)) {
    const answersCopy = answers.map((answer) =>
      answer.id === affirmation.id
        ? {
            ...answer,
            answerEc:
              answerCategory === EstiloAprendizajeTipos.EC
                ? valueAsNumber
                : answer.answerEc,
            answerOr:
              answerCategory === EstiloAprendizajeTipos.OR
                ? valueAsNumber
                : answer.answerOr,
            answerCa:
              answerCategory === EstiloAprendizajeTipos.CA
                ? valueAsNumber
                : answer.answerCa,
            answerEa:
              answerCategory === EstiloAprendizajeTipos.EA
                ? valueAsNumber
                : answer.answerEa,
          }
        : { ...answer }
    );
    setAnswers(answersCopy);
    //}
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isNumeric(e)) e.preventDefault();
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) =>
    e.target.select();

  const handleSiguienteButtonClick = () => {
    //const inCopy = !inAnimation;
    //setInAnimation(inCopy);
    setCurrentStep(2);
  };

  const handleCalcularButtonClick = () => {
    if (
      currentStep === 2 &&
      result.idEncabezado == "00000000-0000-0000-0000-000000000000" &&
      canCalculate &&
      !calculating
    )
      setCalculating(true);
  };

  const handleGeneratePdfButtonClick = () => {
    if (stopGeneratingAutomatically) return;
    if (generating) return;
    setGenerating(true);

    //setTimeout(() => {
    //    setGenerating(false);
    //}, 5000
    //);
    const headers = {
      "Content-Type": "application/json",
      Authorization: "JWT fefege...",
    };

    axios.getH(
      `${EnpointsUrlBase}/EstiloAprendizaje/GenerarInforme` +
        window.location.search,
      {
        headers: {
          "Content-Type": "application/pdf",
        },
      },
      (res: any, data: any) => {
        if (res === 200) {
          setGenerating(false);
          const url = window.URL.createObjectURL(new Blob([data]));

          const link = document.createElement("a");
          link.href = url;
          link.setAttribute(
            "download",
            `${t("pdf.fileName")}(${nombreUsuario})-(${programa}).pdf`
          );

          // Append to html link element page
          document.body.appendChild(link);

          // Start download
          link.click();

          // Clean up and remove the link
          link?.parentNode?.removeChild(link);
        }
      }
    );
  };

  const debouncedHandleGeneratePdfButtonClick = debounce(
    handleGeneratePdfButtonClick,
    500
  );

  return (
    <EstiloAprendizajeMainContainer>
      <div>
        <EstiloAprendizajeHeader className="estilo-aprendizaje-header">
          <TopHeader className={"top-header"}>
            <div>
              <IntrospeccionLabel>
                {t("header.introspection")}
              </IntrospeccionLabel>
              <IntrospeccionName>
                {
                  <div>
                    {t("header.learningStyle")}{" "}
                    <span>{t("header.learningStyleHighlight")} </span>
                    {/*{(esPorEtapa ? `Etapa ${etapa}` : "")}*/}
                  </div>
                }
              </IntrospeccionName>
            </div>
            <LogoContainer>
              <img
                className="logoleft"
                src={
                  require(`../../assets/images/estilo-aprendizaje/logos/1/logoleft.svg`)
                    .default
                }
                alt=""
              />
              <img
                className="logoright"
                src={
                  require(`../../assets/images/estilo-aprendizaje/logos/1/logoright.svg`)
                    .default
                }
                alt=""
              />
            </LogoContainer>
          </TopHeader>
          <div
            style={{
              backgroundColor: "#ffffff",
              color: "#003a4b",
              padding: "0 10px",
              borderBottom: "5px solid #b6b832",
            }}
          >
            {currentStep === 2 && answers.length > 0 && (
              <p className="small-text">
                {t("step2.reminder")}{" "}
                <span className="onlyPc">{t("instructions.row")}</span>
                <span className="onlyMobile">
                  {t("instructions.wordGroups")}
                </span>
                {t("step2.classifyWith4")}
              </p>
            )}
          </div>
        </EstiloAprendizajeHeader>
      </div>

      {loading && (
        <AffirmationContainer>
          <p className="small-text">{t("loading.verifying")}</p>
        </AffirmationContainer>
      )}

      {!loading &&
        currentStep === 1 &&
        result.idEncabezado == "00000000-0000-0000-0000-000000000000" && (
          <AffirmationContainer>
            <ExplanationContainer>
              <h1>
                <span>
                  <div></div>
                  <span>{t("instructions.title")}</span>
                </span>
              </h1>
              <InstruccionesContainer>
                <p>
                  <span>{t("instructions.paragraph1Start")}</span>
                  <strong>
                    {` ${esPorEtapa ? "3" : "9"} `}
                    <span className="onlyPc">{t("instructions.rows")}</span>
                    <span className="onlyMobile">
                      {t("instructions.wordGroups")}
                    </span>
                  </strong>

                  <span>{t("instructions.each")}</span>
                  <span className="onlyPc">{t("instructions.row")}</span>
                  <span className="onlyMobile">{t("instructions.group")}</span>
                  {` ${t("instructions.composedOf")} `}

                  <strong>{t("instructions.words")}</strong>
                </p>
                <p>
                  {t("instructions.paragraph2Start")}{" "}
                  <span className="onlyPc">{t("instructions.row")}</span>{" "}
                  <span className="onlyMobile">{t("instructions.group")} </span>{" "}
                  {t("instructions.usingNumbers")}{" "}
                  <span>
                    {" "}
                    {t("instructions.being")} <strong>4</strong>
                  </span>{" "}
                  {t("instructions.mostIdentify")}{" "}
                  <strong>{t("instructions.mostIdentifyBold")}</strong>{" "}
                  {t("instructions.and")} <strong>1</strong>{" "}
                  <span>{t("instructions.leastIdentify")} </span>{" "}
                  <strong>{t("instructions.leastIdentifyBold")} </strong>
                  <div style={{ marginTop: "20px" }}>
                    <i>
                      {t("instructions.warning")}{" "}
                      <span className="onlyPc">
                        {t("instructions.sameRow")}
                      </span>{" "}
                      <span className="onlyMobile">
                        {t("instructions.sameGroup")}{" "}
                      </span>
                      {t("instructions.noBlankCells")}
                    </i>
                  </div>
                </p>
                <p className="example">{t("instructions.example")}</p>
                <p>
                  <RowContainer>
                    <FirstItemContainer>
                      <span>1</span>
                    </FirstItemContainer>
                    <ItemContainer>
                      <span>{t("example.discriminating")}</span>
                      <input
                        maxLength={1}
                        type="text"
                        disabled={true}
                        value={4}
                      />
                    </ItemContainer>
                    <ItemContainer>
                      <span>{t("example.tentative")}</span>
                      <input
                        maxLength={1}
                        type="text"
                        disabled={true}
                        value={3}
                      />
                    </ItemContainer>
                    <ItemContainer>
                      <span>{t("example.committed")}</span>
                      <input
                        maxLength={1}
                        type="text"
                        disabled={true}
                        value={1}
                      />
                    </ItemContainer>
                    <ItemContainer>
                      <span>{t("example.practical")}</span>
                      <input
                        maxLength={1}
                        type="text"
                        disabled={true}
                        value={2}
                      />
                    </ItemContainer>
                  </RowContainer>
                </p>
              </InstruccionesContainer>
              <h6>{t("step1.thankYou")}</h6>
            </ExplanationContainer>
          </AffirmationContainer>
        )}
      {!loading &&
        currentStep === 2 &&
        result.idEncabezado == "00000000-0000-0000-0000-000000000000" && (
          <AffirmationContainer>
            {answers.length <= 0 && (
              <ExplanationContainer>
                <InstruccionesContainer>
                  <p>{t("step2.serverError")}</p>
                </InstruccionesContainer>
              </ExplanationContainer>
            )}
            {answers.map((affirmation, index) => (
              <RowContainer key={affirmation.id}>
                <FirstItemContainer>
                  <span>{index + 1}</span>
                </FirstItemContainer>

                <ItemContainer>
                  <span>{affirmation.textEc}</span>
                  <input
                    maxLength={1}
                    type="text"
                    onFocus={handleFocus}
                    onKeyDown={(e) => handleKeyDown(e)}
                    onChange={(e) =>
                      handleChange(affirmation, e, EstiloAprendizajeTipos.EC)
                    }
                  />
                </ItemContainer>
                <ItemContainer>
                  <span>{affirmation.textOr}</span>
                  <input
                    maxLength={1}
                    type="text"
                    onFocus={handleFocus}
                    onKeyDown={(e) => handleKeyDown(e)}
                    onChange={(e) =>
                      handleChange(affirmation, e, EstiloAprendizajeTipos.OR)
                    }
                  />
                </ItemContainer>
                <ItemContainer>
                  <span>{affirmation.textCa}</span>
                  <input
                    maxLength={1}
                    type="text"
                    onFocus={handleFocus}
                    onKeyDown={(e) => handleKeyDown(e)}
                    onChange={(e) =>
                      handleChange(affirmation, e, EstiloAprendizajeTipos.CA)
                    }
                  />
                </ItemContainer>
                <ItemContainer>
                  <span>{affirmation.textEa}</span>
                  <input
                    maxLength={1}
                    type="text"
                    onFocus={handleFocus}
                    onKeyDown={(e) => handleKeyDown(e)}
                    onChange={(e) =>
                      handleChange(affirmation, e, EstiloAprendizajeTipos.EA)
                    }
                  />
                </ItemContainer>

                <hr />
              </RowContainer>
            ))}
          </AffirmationContainer>
        )}

      {!loading &&
        (currentStep === 3 ||
          result.idEncabezado != "00000000-0000-0000-0000-000000000000") && (
          <AffirmationContainer>
            <p className="small-text">
              {t("step3.alreadyCompleted")}
              {!stopGeneratingAutomatically && (
                <Fragment>
                  {t("step3.regenerateReport")}{" "}
                  {!generating && (
                    <strong
                      onClick={debouncedHandleGeneratePdfButtonClick}
                      style={{
                        color: "#00abc8",
                        cursor: "pointer",
                      }}
                    >
                      {t("step3.clickHere")}
                    </strong>
                  )}
                </Fragment>
              )}
              {generating && (
                <strong
                  style={{
                    color: "#00abc8",
                  }}
                >
                  {t("step3.generating")}
                </strong>
              )}
            </p>
            <div
              id="radarToExport"
              style={{
                position: "absolute",
                left: "-2000px",
              }}
            >
              <Radar
                width={"500px"}
                height={"500px"}
                data={chartData}
                options={options}
                ref={chartRef}
              />
            </div>
            {!stopGeneratingAutomatically && (
              <div
                style={{
                  position: "relative",
                  backgroundColor: "white",
                  width: "600px",
                  height: "400px",
                  margin: "0 auto",
                  borderRadius: "50px",
                }}
              >
                <div
                  style={{
                    position: "absolute",
                    top: "50px",
                    left: "90px",
                    textAlign: "center",
                  }}
                  className="estilo"
                >
                  <p className={result.estilo === "acomodador" ? "active" : ""}>
                    {t("styles.accommodating")}
                  </p>
                </div>

                <div
                  style={{
                    position: "absolute",
                    top: "50px",
                    right: "90px",
                    textAlign: "center",
                  }}
                  className="estilo"
                >
                  <p className={result.estilo === "divergente" ? "active" : ""}>
                    {t("styles.diverging")}
                  </p>
                </div>

                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <div style={{ width: "400px", height: "400px" }}>
                    <Radar data={chartData} options={options} />
                  </div>
                </div>
                <div
                  style={{
                    position: "absolute",
                    bottom: "50px",
                    left: "90px",
                    textAlign: "center",
                  }}
                  className="estilo"
                >
                  <p
                    className={result.estilo === "convergente" ? "active" : ""}
                  >
                    {t("styles.converging")}
                  </p>
                </div>
                <div
                  style={{
                    position: "absolute",
                    bottom: "50px",
                    right: "90px",
                    textAlign: "center",
                  }}
                  className="estilo"
                >
                  <p className={result.estilo === "asimilador" ? "active" : ""}>
                    {t("styles.assimilating")}
                  </p>
                </div>
              </div>
            )}
          </AffirmationContainer>
        )}

      {!loading && (
        <FooterContainer>
          <ImageButtonContainer
            currentStep={currentStep}
            canCalculate={canCalculate}
            href="#"
            onClick={
              currentStep === 1
                ? handleSiguienteButtonClick
                : currentStep === 2 &&
                  result.idEncabezado ==
                    "00000000-0000-0000-0000-000000000000" &&
                  !calculating
                ? handleCalcularButtonClick
                : currentStep === 3 ||
                  result.idEncabezado != "00000000-0000-0000-0000-000000000000"
                ? debouncedHandleGeneratePdfButtonClick
                : () => {}
            }
          >
            {currentStep === 1 && <span>{t("buttons.continue")}</span>}
            {currentStep === 2 &&
              canCalculate &&
              result.idEncabezado == "00000000-0000-0000-0000-000000000000" &&
              !calculating && <span>{t("buttons.calculate")}</span>}
            {calculating && <span>{t("buttons.processing")}</span>}
            {generating && <span>{t("buttons.generating")}</span>}
            {(currentStep === 3 ||
              result.idEncabezado != "00000000-0000-0000-0000-000000000000") &&
              !generating &&
              !stopGeneratingAutomatically && (
                <span>{t("buttons.viewReport")}</span>
              )}

            {(currentStep === 1 || currentStep === 2 || currentStep === 3) &&
              !stopGeneratingAutomatically && <img src={botonImg} alt="" />}
            {currentStep === 2 && !canCalculate && (
              <span>{t("buttons.fillAllFields")}</span>
            )}
          </ImageButtonContainer>
        </FooterContainer>
      )}
    </EstiloAprendizajeMainContainer>
  );
};

export default EstiloAprendizajeContainer;
